<template>
  <v-card tile>
    <v-card-title class="headline"
      >{{ order ? $t("BAFormEditOrder") + order.id : $t("BAFormBookMulti") }}
    </v-card-title>
    <v-card-text class="pb-0">
      <v-row style="display: flex; align-items: end" class="mt-2">
        <v-col cols="12" md="2" class="pb-0 pt-2">
          <v-menu
            v-model="datepicker"
            :close-on-content-click="true"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
            class="ml-5"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :label="$t('BAFormBookDate') + ' *'"
                type="text"
                :value="formattedDate"
                dense
                readonly
                v-bind="attrs"
                v-on="on"
                required
                :error-messages="dateErrors"
                :disabled="isLoading"
              ></v-text-field>
            </template>
            <v-date-picker
              :min="moment().format('YYYY-MM-DD')"
              @change="changeFormItems()"
              v-model="currentOrder.date"
              no-title
              scrollable
              :locale="$i18n.locale"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="2" class="pb-0 pt-2">
          <v-select
            v-if="!isLoading"
            :items="scheduleTimes"
            v-model="selectedTime"
            dense
            :label="$t('BAFormTimeBegin')"
            item-text="name"
            item-value="id"
            :no-data-text="$t('BAFormNoSessions')"
          ></v-select>
        </v-col>
        <v-col cols="12" md="2" class="py-0">
          <v-select
            v-if="!isLoading"
            :items="checkboxes"
            item-text="label"
            item-value="type"
            :label="$t('BAFormPlayground')"
            v-model="selectedCheckboxes"
            dense
            multiple
            @change="handleSelectionChange"
          >
            <template slot="no-data">
              <span class="grey--text caption ml-1">{{
                $t("placeholderLoading")
              }}</span>
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="index === 0">{{ item.label }}</span>
              <span v-if="index === 1" class="grey--text caption ml-1"
                >(+{{ selectedCheckboxes.length - 1 }})</span
              >
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-action>
                <v-checkbox
                  :input-value="selectedCheckboxes.includes(item.type)"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title v-text="item.label"></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-select>
        </v-col>
        <v-layout style="padding: 0 10px 0" class="mobile-column">
          <v-flex style="margin-right: 50px; width: 100%">
            <v-card-title class="headline pt-0">{{
              $t("BAFormBookAvailable")
            }}</v-card-title>
            <v-data-table
              :headers="headers"
              fixed-header
              height="370px"
              dense
              :items="filteredLocationSchedule"
              :items-per-page="-1"
              class="elevation-1"
              hide-default-footer
              :loading="isLoading"
              :loading-text="$t('BAStandBy')"
              :no-data-text="$t('BAFormNoSessions')"
            >
              <template v-slot:item="{ item }">
                <tr @click="addSession(item)" style="cursor: pointer">
                  <td>
                    <span>{{ item.playground_name }}</span>
                  </td>
                  <td>
                    <span>{{ item.begin | timeFormat }}</span>
                  </td>
                  <td>
                    <span>{{ item.time_end }}</span>
                  </td>
                  <td>
                    <span>{{ item.available_persons }}</span>
                  </td>
                  <td>
                    <span>{{
                      Number(
                        item.pricing.personal_price
                          ? item.pricing.personal_price * item.available_persons
                          : item.pricing.session_price
                      ).toFixed(2)
                    }}</span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex style="width: 100%">
            <v-card-title class="headline pt-0">{{
              $t("BAFormBookInOrder")
            }}</v-card-title>
            <v-data-table
              :headers="headers"
              fixed-header
              height="370px"
              dense
              :items="selectedSessions"
              :items-per-page="-1"
              class="elevation-1"
              hide-default-footer
              :no-data-text="$t('BANoSessionsSelected')"
            >
              <template v-slot:item="{ item }">
                <tr @click="removeSession(item)" style="cursor: pointer">
                  <td>
                    <span>{{ item.playground_name }}</span>
                  </td>
                  <td>
                    <span>{{ item.begin | timeFormat }}</span>
                  </td>
                  <td>
                    <span>{{ item.time_end }}</span>
                  </td>
                  <td>
                    <span>{{ item.available_persons }}</span>
                  </td>
                  <td>
                    <span>{{
                      Number(
                        item.pricing.personal_price
                          ? item.pricing.personal_price * item.available_persons
                          : item.pricing.session_price
                      ).toFixed(2)
                    }}</span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
        <v-col cols="12" sm="4" class="pb-0 pt-6">
          <v-text-field
            v-model="currentOrder.name"
            :label="$t('BAFormName') + ' *'"
            type="text"
            dense
            required
            :error-messages="nameErrors"
            @input="$v.currentOrder.name.$touch()"
            @blur="$v.currentOrder.name.$touch()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" class="pb-0 pt-6">
          <v-text-field
            v-model="currentOrder.phone"
            :label="$t('BAFormPhone') + ' *'"
            type="text"
            dense
            required
            :error-messages="phoneErrors"
            @input="$v.currentOrder.phone.$touch()"
            @blur="$v.currentOrder.phone.$touch()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" class="pb-0 pt-6">
          <v-text-field
            v-model="currentOrder.email"
            :label="'E-mail' + ' *'"
            type="text"
            dense
            required
            :error-messages="emailErrors"
            @input="$v.currentOrder.email.$touch()"
            @blur="$v.currentOrder.email.$touch()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <v-select
            :items="filteredOrderTypes"
            v-model="currentOrder.order_type"
            dense
            item-text="title"
            item-value="id"
            @change="selectionOrderType"
            :label="$t('BAFormType') + ' *'"
            :disabled="Boolean(order)"
            required
            :error-messages="orderTypeErrors"
          ></v-select>
        </v-col>
        <v-col
          v-if="currentOrder.order_type === 4"
          cols="12"
          sm="4"
          class="pb-0"
        >
          <v-select
            :items="eventTypes"
            v-model="currentOrder.event_type_id"
            dense
            :label="$t('BAFormEvents') + ' *'"
            :item-text="(item) => item.name + '  ' + item.price"
            item-value="id"
            @change="changeFormEvents()"
            :disabled="Boolean(order)"
            required
          ></v-select>
        </v-col>
        <v-col
          v-if="
            currentOrder.order_type === 1 || currentOrder.order_type === null
          "
          cols="12"
          sm="4"
          class="pb-0"
        >
          <v-text-field
            v-model="currentOrder.cost"
            :label="$t('BAFormSum') + ' *'"
            type="text"
            dense
            required
            :error-messages="costErrors"
            @input="$v.currentOrder.cost.$touch()"
            @blur="$v.currentOrder.cost.$touch()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <v-text-field
            v-model="currentOrder.persons"
            :label="$t('BAFormPersons') + ' *'"
            type="text"
            dense
            required
            :error-messages="personsErrors"
            @input="$v.currentOrder.persons.$touch()"
            @blur="$v.currentOrder.persons.$touch()"
            :disabled="Boolean(order)"
          ></v-text-field>
        </v-col>
        <v-col
          v-if="currentOrder.order_type === 4 && !this.order"
          cols="12"
          sm="4"
          class="pb-0"
        >
          <v-text-field
            :label="$t('BAFormPromocode')"
            type="text"
            v-model="currentOrder.promocode"
            dense
            :disabled="!currentOrder.time"
            append-outer-icon="mdi-send"
            @click:append-outer="validatePromocode"
            @change="validatePromocode"
            :error-messages="promocodeErrors"
          ></v-text-field>
        </v-col>
        <v-col
          v-if="currentOrder.order_type === 4 && !this.order"
          cols="12"
          sm="4"
          class="pb-0"
        >
          <p
            style="font-size: 16px"
            class="mt-2 font-weight-medium"
            v-html="$t('BAFormSum') + computedPrice"
          ></p>
        </v-col>
        <v-col v-if="!this.order" cols="12" sm="4" class="pb-0">
          <v-text-field
            v-model="currentOrder.seller"
            :label="$t('BAFormSeller')"
            type="text"
            dense
          ></v-text-field>
        </v-col>
        <v-col v-if="!this.order" cols="12" sm="4" class="pb-0">
          <v-checkbox
            class="mt-1"
            v-model="currentOrder.bill"
            :label="$t('BAFormBill')"
            :disabled="
              currentOrder.order_type !== 1 && currentOrder.order_type !== 4
            "
          ></v-checkbox>
        </v-col>
        <v-col
          v-if="currentOrder.bill && !this.order"
          cols="12"
          sm="4"
          class="pb-0"
        >
          <v-text-field
            v-model="currentOrder.cancel_delay"
            :label="$t('BAFormBillActivity')"
            type="number"
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" class="pb-0 pt-0">
          <v-textarea
            rows="1"
            v-model="currentOrder.comment"
            :label="$t('BAFormComment')"
            auto-grow
            dense
          ></v-textarea>
        </v-col>
      </v-row>
      <small>* {{ $t("validation") }}</small>
    </v-card-text>
    <v-card-actions class="pt-0">
      <v-spacer></v-spacer>
      <v-btn
        v-if="!onOrderCreatePage"
        color="red darken-1"
        text
        @click="closeForm()"
        >{{ $t("btnExit") }}</v-btn
      >
      <v-btn
        color="green darken-1"
        text
        @click="createMultibook()"
        :disabled="
          $v.currentOrder.$invalid ||
          $v.currentOrder.date.$dirty ||
          disabledBtn ||
          selectedSessions.length === 0
        "
      >
        {{ order ? $t("btnSave") : $t("btnCreate") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, requiredIf, email, helpers } from "vuelidate/lib/validators";
import moment from "moment";

const promoValid = (value) => {
  return !!value;
};
const phoneValid = (value) => {
  return !!value;
};
const phoneRegexp = helpers.regex("phone", /^\+?[0-9]+$/);
const costRegexp = helpers.regex("cost", /^[0-9,.]+$/);
const personsRegexp = helpers.regex("cost", /^[0-9]+$/);

export default {
  name: "BookingAdminFormMulti",

  mixins: [validationMixin],

  props: [
    "order",
    "dialogFormMulti",
    "selectedDay",
    "onOrderCreatePage",
    "amoId",
  ],

  data() {
    return {
      datepicker: false,
      originalDate: null,
      checkboxes: [],
      selectedCheckboxes: [],
      selectedPlaygrounds: [],
      selectedTime: null,
      disabledBtn: false,
      currentOrder: {
        date: moment().format("YYYY-MM-DD"),
        cost: null,
        persons: null,
        name: "",
        email: "",
        phone: "",
        promocode: "",
        bill: false,
        cancel_delay: 25,
        order_type: null,
        event_type_id: 0,
        time: null,
        comment: "",
        location_id: null,
        playsessions: [],
        seller: null,
        amo_id: null,
        source: "business.anvio.com",
      },
      responseSchedule: [],
      responsePlaygrounds: [],
      multibookPlaygrounds: [],
      headers: [
        {
          text: this.$i18n.t("groupPlayground"),
          value: "playground_name",
          width: "20%",
        },
        { text: this.$i18n.t("groupBegin"), value: "begin", width: "10%" },
        { text: this.$i18n.t("groupEnd"), value: "end", width: "10%" },
        {
          text: this.$i18n.t("groupPlaces"),
          value: "available_persons",
          width: "10%",
        },
        { text: this.$i18n.t("groupPrice"), value: "pricing", width: "10%" },
      ],
      order_types: [
        {
          id: 1,
          title: this.$i18n.t("BAFormPaid"),
        },
        {
          id: 2,
          title: this.$i18n.t("BAFormTech"),
        },
        {
          id: 3,
          title: this.$i18n.t("BAFormFree"),
        },
        {
          id: 4,
          title: this.$i18n.t("BAFormEvent"),
        },
      ],
      playground_names: {
        arena: this.$i18n.t("BAArena"),
        the_cube: this.$i18n.t("BACube"),
        lounge: this.$i18n.t("BALounge"),
      },
      phoneValid: true,
      locationSchedule: [],
      selectedSessions: [],
      sessionSorting: {
        prop: null,
        asc: null,
      },
      selectedSessionSorting: {
        prop: null,
        asc: null,
      },
      eventTypes: [],
      eventPrice: [],
      discountValid: true,
      discountValue: false,
      discountType: false,
      isLoading: true,
    };
  },

  validations: {
    currentOrder: {
      name: {
        required,
      },
      phone: {
        required,
        phoneRegexp,
      },
      email: {
        required,
        email,
      },
      date: {
        required: requiredIf(function () {
          return !this.order;
        }),
      },
      cost: {
        required,
        costRegexp,
      },
      persons: {
        required,
        personsRegexp,
      },
      order_type: {
        required,
      },
    },
    discountValid: {
      promoValid,
    },
    phoneValid: {
      phoneValid,
    },
  },

  computed: {
    ...mapGetters([
      "locationId",
      "location",
      "events",
      "BAMultibookSchedule",
      "BAMultibookOrder",
    ]),
    computedPrice() {
      let price = 0;

      if (this.discountType < 1) {
        price =
          this.currentOrder.cost * (1 - this.discountValue * this.discountType);
      } else {
        price = this.currentOrder.cost - this.discountValue * this.discountType;
      }

      return price > 0 ? price : 0;
    },
    formattedDate() {
      return moment.parseZone(this.currentOrder.date).format("DD MMMM YYYY");
    },
    completedDate() {
      return (
        moment.parseZone(this.currentOrder.date).format("YYYY-MM-DD") +
        " " +
        this.currentOrder.time
      );
    },
    scheduleTimes() {
      const uniqueTimes = new Set(
        this.locationSchedule.map((item) => item.time_start)
      );
      return [...uniqueTimes].sort((a, b) => {
        const aHour = Number(a.split(":")[0]);
        const bHour = Number(b.split(":")[0]);
        const aMin = Number(a.split(":")[1]);
        const bMin = Number(b.split(":")[1]);

        if (aHour <= 6 && bHour <= 6) return aHour - bHour || aMin - bMin;
        if (aHour <= 6) return 1;
        if (bHour <= 6) return -1;
        return aHour - bHour || aMin - bMin;
      });
    },
    filteredLocationSchedule() {
      return this.locationSchedule.filter((item) => {
        const idCondition =
          this.selectedPlaygrounds.length === 0 ||
          this.selectedPlaygrounds.includes(item.playground);
        let timeCondition;

        if (!this.selectedTime) {
          timeCondition = true;
        } else if (
          this.selectedTime >= "00:00" &&
          this.selectedTime <= "06:00"
        ) {
          if (item.time_start >= "00:00" && item.time_start <= "06:00") {
            timeCondition = item.time_start >= this.selectedTime;
          } else {
            timeCondition = false;
          }
        } else {
          timeCondition =
            item.time_start >= this.selectedTime ||
            (item.time_start >= "00:00" && item.time_start <= "06:00");
        }

        return idCondition && timeCondition;
      });
    },
    filteredOrderTypes() {
      if (this.location.is_park || this.location.is_events_available) {
        return this.order_types;
      } else {
        return this.order_types.filter((orderType) => orderType.id !== 4);
      }
    },
    dateErrors() {
      const errors = [];
      if (!this.$v.currentOrder.date.$dirty) return errors;
      !this.$v.currentOrder.date.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.currentOrder.name.$dirty) return errors;
      !this.$v.currentOrder.name.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    promocodeErrors() {
      const errors = [];
      if (!this.$v.discountValid.$dirty) return errors;
      !this.$v.discountValid.promoValid &&
        errors.push(this.$i18n.t("validationPromocode"));
      return errors;
    },
    phoneErrors() {
      const errors = [];
      if (!this.$v.currentOrder.phone.$dirty) return errors;
      !this.$v.currentOrder.phone.required &&
        errors.push(this.$i18n.t("validationRequired"));
      !this.$v.currentOrder.phone.phoneRegexp &&
        errors.push(this.$i18n.t("validationPhone"));
      !this.$v.phoneValid.phoneValid &&
        errors.push(this.$i18n.t("validationPhone"));
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.currentOrder.email.$dirty) return errors;
      !this.$v.currentOrder.email.email &&
        errors.push(this.$i18n.t("validationEmail"));
      !this.$v.currentOrder.email.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
    costErrors() {
      const errors = [];
      if (!this.$v.currentOrder.cost.$dirty) return errors;
      !this.$v.currentOrder.cost.required &&
        errors.push(this.$i18n.t("validationRequired"));
      !this.$v.currentOrder.cost.costRegexp &&
        errors.push(this.$i18n.t("validationCost"));
      return errors;
    },
    personsErrors() {
      const errors = [];
      if (!this.$v.currentOrder.persons.$dirty) return errors;
      !this.$v.currentOrder.persons.required &&
        errors.push(this.$i18n.t("validationRequired"));
      !this.$v.currentOrder.persons.personsRegexp &&
        errors.push(this.$i18n.t("validationPersons"));
      return errors;
    },
    orderTypeErrors() {
      const errors = [];
      if (!this.$v.currentOrder.order_type.$dirty) return errors;
      !this.$v.currentOrder.order_type.required &&
        errors.push(this.$i18n.t("validationRequired"));
      return errors;
    },
  },
  filters: {
    dateFormat: (date) => {
      return moment.parseZone(date).format("DD.MM.YYYY");
    },
    timeFormat: (date) => {
      return moment.parseZone(date).format("HH:mm");
    },
  },
  mounted() {
    this.currentOrder.location_id = this.locationId;
    document.addEventListener("keyup", this.handleKeyup);
  },
  beforeDestroy() {
    document.removeEventListener("keyup", this.handleKeyup);
  },
  methods: {
    ...mapActions([
      "createOrderMulti",
      "editOrderMulti",
      "resetState",
      "checkCoupon",
      "loadEvents",
      "loadBAMultibookSchedule",
      "loadBAMultibookOrder",
    ]),
    addSession(item) {
      const index = this.locationSchedule.indexOf(item);
      if (index > -1) {
        this.locationSchedule.splice(index, 1);
      }

      this.selectedSessions.push(item);

      if (this.currentOrder.order_type !== 4) {
        this.currentOrder.cost = this.selectedSessions.reduce(
          (acc, o) =>
            acc +
            parseInt(
              o.pricing.personal_price
                ? o.pricing.personal_price * o.available_persons
                : o.pricing.session_price
            ),
          0
        );
      }
      if (
        this.currentOrder.order_type === 2 &&
        this.currentOrder.order_type === 3
      ) {
        this.currentOrder.cost = 0;
      }
      this.sortEventsTimeForPromocode();
    },
    removeSession(item) {
      if (this.responseSchedule.length) {
        const index = this.selectedSessions.indexOf(item);
        if (index > -1) {
          this.selectedSessions.splice(index, 1);
        }

        this.locationSchedule.push(item);

        if (this.currentOrder.order_type !== 4) {
          this.currentOrder.cost = this.selectedSessions.reduce(
            (acc, o) =>
              acc +
              parseInt(
                o.pricing.personal_price
                  ? o.pricing.personal_price * o.available_persons
                  : o.pricing.session_price
              ),
            0
          );
        }
        if (
          this.currentOrder.order_type === 2 &&
          this.currentOrder.order_type === 3
        ) {
          this.currentOrder.cost = 0;
        }
        this.sortEventsTimeForPromocode();
      }
    },
    sortSessions(item, asc) {
      if (asc == null) {
        if (
          this.sessionSorting.prop == null &&
          this.sessionSorting.asc == null
        ) {
          this.sessionSorting.prop = item;
          this.sessionSorting.asc = true;
        } else if (this.sessionSorting.prop === item) {
          this.sessionSorting.asc = !this.sessionSorting.asc;
        } else if (this.sessionSorting.prop !== item) {
          this.sessionSorting.prop = item;
          this.sessionSorting.asc = true;
        }
      } else {
        this.sessionSorting.prop = item;
        this.sessionSorting.asc = asc;
      }

      if (this.sessionSorting.prop === "time" && this.sessionSorting.asc) {
        this.locationSchedule.sort(
          (a, b) => new Date(a.begin) - new Date(b.begin)
        );
      } else if (
        this.sessionSorting.prop === "time" &&
        !this.sessionSorting.asc
      ) {
        this.locationSchedule.sort(
          (a, b) => new Date(b.begin) - new Date(a.begin)
        );
      } else if (
        this.sessionSorting.prop === "playground" &&
        this.sessionSorting.asc
      ) {
        this.locationSchedule.sort(
          (a, b) =>
            a.playground_name.localeCompare(b.playground_name) ||
            new Date(a.begin) - new Date(b.begin)
        );
      } else if (
        this.sessionSorting.prop === "playground" &&
        !this.sessionSorting.asc
      ) {
        this.locationSchedule.sort(
          (a, b) =>
            b.playground_name.localeCompare(a.playground_name) ||
            new Date(a.begin) - new Date(b.begin)
        );
      }
    },
    sortSelectedSessions(item, asc) {
      if (asc == null) {
        if (
          this.selectedSessionSorting.prop == null &&
          this.selectedSessionSorting.asc == null
        ) {
          this.selectedSessionSorting.prop = item;
          this.selectedSessionSorting.asc = true;
        } else if (this.selectedSessionSorting.prop === item) {
          this.selectedSessionSorting.asc = !this.selectedSessionSorting.asc;
        } else if (this.selectedSessionSorting.prop !== item) {
          this.selectedSessionSorting.prop = item;
          this.selectedSessionSorting.asc = true;
        }
      } else {
        this.selectedSessionSorting.prop = item;
        this.selectedSessionSorting.asc = asc;
      }

      if (
        this.selectedSessionSorting.prop === "time" &&
        this.selectedSessionSorting.asc
      ) {
        this.selectedSessions.sort(
          (a, b) => new Date(a.begin) - new Date(b.begin)
        );
      } else if (
        this.selectedSessionSorting.prop === "time" &&
        !this.selectedSessionSorting.asc
      ) {
        this.selectedSessions.sort(
          (a, b) => new Date(b.begin) - new Date(a.begin)
        );
      } else if (
        this.selectedSessionSorting.prop === "playground" &&
        this.selectedSessionSorting.asc
      ) {
        this.selectedSessions.sort(
          (a, b) =>
            a.playground_name.localeCompare(b.playground_name) ||
            new Date(a.begin) - new Date(b.begin)
        );
      } else if (
        this.selectedSessionSorting.prop === "playground" &&
        !this.selectedSessionSorting.asc
      ) {
        this.selectedSessions.sort(
          (a, b) =>
            b.playground_name.localeCompare(a.playground_name) ||
            new Date(a.begin) - new Date(b.begin)
        );
      }
    },
    async loadMultibookSchedule() {
      const formattedDate = moment
        .parseZone(this.currentOrder.date)
        .format("YYYY-MM-DD");
      await this.loadBAMultibookSchedule(formattedDate);

      const { schedule, playgrounds } = this.BAMultibookSchedule;

      this.responseSchedule = schedule;
      this.responsePlaygrounds = playgrounds;

      const isTargetType = (type) =>
        ["the_cube", "arena", "lounge"].includes(type);
      this.multibookPlaygrounds = playgrounds.filter((playground) =>
        isTargetType(playground.types)
      );

      const playgroundIds = this.multibookPlaygrounds.map(
        (playground) => playground.id
      );
      const playgroundIdToMaxPersons = this.multibookPlaygrounds.reduce(
        (acc, playground) => {
          acc[playground.id] = playground.max_persons;
          return acc;
        },
        {}
      );
      const playgroundIdToName = this.multibookPlaygrounds.reduce(
        (acc, playground) => {
          acc[playground.id] = playground.name;
          return acc;
        },
        {}
      );

      this.locationSchedule = schedule
        .filter(
          (session) =>
            playgroundIds.includes(session.playground) &&
            session.available_persons ===
              playgroundIdToMaxPersons[session.playground]
        )
        .map((session) => {
          session.playground_name = playgroundIdToName[session.playground];
          return session;
        });

      this.isLoading = false;
      this.sortSessions("playground", true);
    },
    async loadMultibookOrder() {
      await this.loadBAMultibookOrder(this.order.id);

      if (this.responseSchedule.length) {
        const playgroundIdToDetails = this.multibookPlaygrounds.reduce(
          (acc, playground) => {
            acc[playground.id] = {
              name: playground.name,
              max_persons: playground.max_persons,
            };
            return acc;
          },
          {}
        );

        this.selectedSessions = this.responseSchedule
          .filter((session) =>
            this.BAMultibookOrder.playsessions.some(
              (orderSession) =>
                session.playground === orderSession.playground_id &&
                moment.parseZone(session.begin).format("DD.MM.YYYY HH:mm") ===
                  moment
                    .parseZone(orderSession.session_dt)
                    .format("DD.MM.YYYY HH:mm")
            )
          )
          .map((session) => ({
            ...session,
            playground_name: playgroundIdToDetails[session.playground].name,
            max_persons: playgroundIdToDetails[session.playground].max_persons,
            available_persons:
              playgroundIdToDetails[session.playground].max_persons,
          }));
      } else {
        const playgroundIdToName = this.responsePlaygrounds.reduce(
          (acc, playground) => {
            acc[playground.id] = playground.name;
            return acc;
          },
          {}
        );

        this.selectedSessions = this.BAMultibookOrder.playsessions.map(
          (orderSession) => ({
            available_persons: " ",
            begin: orderSession.session_dt,
            end: " ",
            pricing: " ",
            playground_name: playgroundIdToName[orderSession.playground_id],
          })
        );
      }
      this.sortSelectedSessions("playground", true);
    },
    async changeFormItems() {
      this.isLoading = true;
      this.selectedTime = null;
      this.locationSchedule = [];
      this.selectedSessions = [];
      this.selectedPlaygrounds = [];
      this.selectedCheckboxes = [];
      this.currentOrder.cost = null;
      if (!this.order) {
        this.currentOrder.order_type = null;
      }
      await this.loadMultibookSchedule();
      this.createGroupedCheckboxes();
    },
    changeFormEvents() {
      let selectedEvent = this.eventTypes.find(
        (x) => x.id === this.currentOrder.event_type_id
      );
      this.currentOrder.cost = selectedEvent.price;
    },
    sortEventsTimeForPromocode() {
      const minTime = this.selectedSessions.sort(
        (a, b) => new Date(a.begin) - new Date(b.begin)
      );
      this.currentOrder.time = minTime.length ? minTime[0].time_start : null;
      this.currentOrder.promocode = "";
      this.discountValid = true;
      this.discountValue = false;
      this.discountType = false;
    },
    clearForm() {
      this.currentOrder.cost = null;
      this.phoneValid = true;
      this.currentOrder.name = "";
      this.currentOrder.phone = "";
      this.currentOrder.email = "";
      this.currentOrder.persons = null;
      this.currentOrder.order_type = null;
      this.currentOrder.bill = false;
      this.currentOrder.cancel_delay = null;
      this.currentOrder.comment = null;
      this.currentOrder.seller = null;
      this.locationSchedule = [];
      this.selectedPlaygrounds = [];
      this.selectedCheckboxes = [];
      this.selectedTime = null;
      this.selectedSessions = [];
      this.disabledBtn = false;
      this.currentOrder.cost = null;
      this.currentOrder.promocode = "";
      this.discountValid = true;
      this.discountValue = false;
      this.discountType = false;
      this.isLoading = true;

      this.$v.$reset();
    },
    createMultibook() {
      this.disabledBtn = true;
      if (this.order) {
        this.currentOrder.playsessions = this.selectedSessions.map((e) => ({
          playground_id: e.playground,
          session_dt: moment.parseZone(e.begin).format("YYYY-MM-DD HH:mm"),
        }));
        this.editOrderMulti(this.currentOrder)
          .then(() => {
            this.$emit("reload-schedule");
            this.$emit("close-dialog");
            this.disabledBtn = false;
          })
          .catch((error) => {
            this.phoneValid = !error.response.data.phone;
            this.$v.phoneValid.$touch();
          });
      } else {
        this.currentOrder.playsessions = this.selectedSessions.map((e) => ({
          playground_id: e.playground,
          session_dt: moment.parseZone(e.begin).format("YYYY-MM-DD HH:mm"),
        }));
        this.createOrderMulti(this.currentOrder)
          .then(() => {
            this.$emit("close-dialog");
            this.$emit("visible-alert");
            this.disabledBtn = false;
          })
          .catch((error) => {
            this.phoneValid = !error.response.data.phone;
            this.$v.phoneValid.$touch();
          });
      }
    },
    closeForm() {
      this.$emit("close-form");
    },
    loadEventTypes() {
      this.loadEvents({
        id: this.locationId,
        date: this.currentOrder.date,
      }).then(() => {
        this.eventTypes = this.events;
        this.eventPrice = this.eventTypes.map((e) => ({
          price: e.price,
        }));
      });
    },
    selectionOrderType() {
      this.currentOrder.event_type_id = "";
      this.currentOrder.cost = null;

      if (this.currentOrder.order_type !== 4) {
        this.currentOrder.cost = this.selectedSessions.reduce(
          (acc, o) =>
            acc +
            parseInt(
              o.pricing.personal_price
                ? o.pricing.personal_price * o.available_persons
                : o.pricing.session_price
            ),
          0
        );
      }

      if (
        this.currentOrder.order_type !== 1 &&
        this.currentOrder.order_type !== 4
      ) {
        this.currentOrder.bill = false;
      }

      if (
        this.currentOrder.order_type === 2 &&
        this.currentOrder.order_type === 3
      ) {
        this.currentOrder.cost = 0;
      }

      if (this.currentOrder.order_type === 4) {
        this.loadEventTypes();
      }
    },
    validatePromocode() {
      if (!this.currentOrder.promocode) {
        this.discountValid = true;
        this.discountValue = false;
        this.discountType = false;
        return;
      }

      this.checkCoupon({
        code: this.currentOrder.promocode,
        location: this.locationId,
        datetime: this.completedDate,
      }).then((response) => {
        this.discountValid = response.data.valid;
        this.discountType = response.data.type;
        this.discountValue = response.data.discount;
        this.$v.discountValid.$touch();
      });
    },
    groupPlaygroundsByType() {
      const groupedPlaygrounds = {};

      this.multibookPlaygrounds.forEach((playground) => {
        const type = playground.types;
        if (!groupedPlaygrounds[type]) {
          groupedPlaygrounds[type] = [];
        }
        groupedPlaygrounds[type].push(playground.id);
      });

      return groupedPlaygrounds;
    },
    createGroupedCheckboxes() {
      const groupedPlaygrounds = this.groupPlaygroundsByType();
      this.checkboxes = Object.entries(groupedPlaygrounds).map(
        ([type, ids]) => ({
          type: type,
          ids: ids,
          label: this.playground_names[type] || type,
          status: false,
        })
      );
    },
    handleSelectionChange() {
      this.selectedPlaygrounds = [];
      this.selectedCheckboxes.forEach((selectedType) => {
        const checkbox = this.checkboxes.find(
          (chk) => chk.type === selectedType
        );
        if (checkbox) {
          this.selectedPlaygrounds = [
            ...this.selectedPlaygrounds,
            ...checkbox.ids.filter(
              (id) => !this.selectedPlaygrounds.includes(id)
            ),
          ];
        }
      });
    },
    handleKeyup(event) {
      if (event.key === "Escape" || event.keyCode === 27) {
        this.$emit("close-form");
      }
    },
  },
  watch: {
    dialogFormMulti: {
      handler: async function (visible) {
        if (visible) {
          if (this.order) {
            this.currentOrder.id = this.order.id;
            this.currentOrder.cost = this.order.cost;
            this.currentOrder.persons = this.order.persons;
            this.currentOrder.order_type = this.order.order_type;
            this.currentOrder.date = moment(this.selectedDay).format(
              "YYYY-MM-DD"
            );
            this.originalDate = this.currentOrder.date;
            this.currentOrder.name = this.order.name;
            this.currentOrder.phone = this.order.phone;
            this.currentOrder.email = this.order.email;
            this.currentOrder.comment = "";
            if (this.order.order_type === 4) {
              this.loadEventTypes();
              this.currentOrder.event_type_id = this.order.lime_event_type_id;
            }
            await this.loadMultibookSchedule();
            await this.loadMultibookOrder();
            this.createGroupedCheckboxes();
            return;
          }
          await this.loadMultibookSchedule();
          this.createGroupedCheckboxes();
          this.resetState();
        } else {
          this.clearForm();
        }
      },
      immediate: true,
    },
    amoId(newVal) {
      this.currentOrder.amo_id = newVal;
    },
  },
};
</script>

<style>
@media screen and (max-width: 768px) {
  .mobile-column {
    flex-direction: column;
    gap: 20px;
    font-size: 10px;
  }
  td {
    padding: 0 8px !important;
  }
}
</style>
